import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { fireAndForget } from '../utils/fireAndForget';
import { Pill } from '../design-system/components';

interface ArticleCardProps {
  title: string;
  slug: string;
  category: string;
  readingTime: number;
  excerpt?: string;
  index?: number;
  compact?: boolean;
  /**
   * default: the card /learn and the quiz pages render. landing: the
   * landing's design-system card (redesign PR10, design-system/pages/
   * landing.css). Same link, heading and text in both.
   */
  look?: 'default' | 'landing';
}

/** The scroll reveal both looks share, staggered by the card's index. */
function revealProps(index: number) {
  return {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, delay: index * 0.12 },
  };
}

export default function ArticleCard({
  title,
  slug,
  category,
  readingTime,
  excerpt,
  index = 0,
  compact = false,
  look = 'default',
}: ArticleCardProps) {
  const categoryLabel = category.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const url = `/learn/${slug}`;

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (Capacitor.isNativePlatform()) {
      e.preventDefault();
      await Browser.open({
        url: `${import.meta.env.VITE_APP_URL ?? 'https://empathease.app'}${url}`,
      });
    }
  };

  const reveal = revealProps(index);

  if (look === 'landing') {
    return (
      <motion.a
        href={url}
        onClick={fireAndForget(handleClick)}
        className="ee-landing-article"
        {...reveal}
      >
        <div className="ee-landing-article__meta">
          <Pill variant="accent">{categoryLabel}</Pill>
          <span className="ee-landing-article__time">{readingTime} min read</span>
        </div>

        <h3 className="ee-landing-article__title">{title}</h3>

        {excerpt ? <p className="ee-landing-article__excerpt">{excerpt}</p> : null}

        <div className="ee-landing-article__more">
          Read article
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </motion.a>
    );
  }

  return (
    <motion.a
      href={url}
      onClick={fireAndForget(handleClick)}
      className={`block bg-(--ee-surface) rounded-xl border border-base-200 hover:shadow-md transition-shadow group ${
        compact ? 'p-4' : 'p-6 shadow-sm'
      }`}
      {...reveal}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
          {categoryLabel}
        </span>
        <span className="text-xs text-base-content/50">{readingTime} min read</span>
      </div>

      <h3
        className={`font-bold text-base-content group-hover:text-primary transition-colors leading-snug ${
          compact ? 'text-sm' : 'text-base'
        }`}
      >
        {title}
      </h3>

      {excerpt && !compact ? (
        <p className="mt-2 text-sm text-base-content/60 line-clamp-2 leading-relaxed">{excerpt}</p>
      ) : null}

      <div className="mt-3 flex items-center gap-1 text-xs text-primary font-medium">
        Read article
        <svg
          className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </motion.a>
  );
}
